import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { CheckCircle, Cancel, Event, Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  confirmAppointment,
  rescheduleAppointment,
  cancelAppointment,
  fetchAppointments,
} from '../../features/appointments/appointmentsSlice';
import { fetchAvailability } from '../../features/booking/bookingSlice';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const AppointmentPopup = ({ open, handleClose, appointment, onUpdate }) => {
  const dispatch = useDispatch();
  const { availability, loading } = useSelector((state) => state.booking);

  let slot_duration = 30;

  if (availability.available_slots && availability.available_slots.length > 0) {
    console.log(availability);
    slot_duration = availability.slot_duration;
  }

  let available_slots = [];

  if (availability.available_slots && availability.available_slots.length > 0) {
    available_slots = availability.available_slots;
  }

  const [actionType, setActionType] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const navigate = useNavigate();

  const handleStartConsultation = () => {
    if (!appointment?.id || !appointment?.patient) {
      toast.error('Missing appointment or patient details.');
      return;
    }

    // ✅ Navigate to Enhanced Consultation Page with Appointment ID & Patient ID
    const patientId = appointment.patient?.id || appointment.patient || appointment.patient_id;
    navigate(
      `/doctor-dashboard/enhanced-consultation/${appointment.id}/${patientId}`
    );
  };

  //   return (
  //     <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
  //       {/* Other UI elements remain unchanged */}

  //       {/* ✅ START CONSULTATION BUTTON (Enabled only if appointment is not canceled or completed) */}
  //       {['Pending', 'Confirmed'].includes(appointment?.status) && (
  //         <Button
  //           variant="contained"
  //           color="primary"
  //           fullWidth
  //           sx={{ mt: 2 }}
  //           onClick={handleStartConsultation} // ✅ Navigate to Consultation Page
  //         >
  //           Start Consultation
  //         </Button>
  //       )}
  //     </Dialog>
  //   );
  // };

  useEffect(() => {
    if (appointment) {
      setRescheduleDate(appointment.scheduled_date || '');
      setRescheduleTime(appointment.scheduled_time || '');
    }
  }, [appointment]);

  useEffect(() => {
    if (rescheduleDate && appointment) {
      // Extract doctor ID - handle both object and ID formats
      const doctorId = appointment.doctor_id || 
                      (typeof appointment.doctor === 'object' ? appointment.doctor?.id : appointment.doctor) ||
                      (appointment.doctor?.user?.id);
      
      if (doctorId) {
        dispatch(
          fetchAvailability({
            doctorId: doctorId,
            date: rescheduleDate,
          })
        );
      }
    }
  }, [rescheduleDate, appointment, dispatch]);

  const handleConfirmAction = async () => {
    if (!actionType) return;

    try {
      if (actionType === 'confirm') {
        const response = await dispatch(
          confirmAppointment(appointment.id)
        ).unwrap();
        toast.success('Appointment confirmed successfully!');
      } else if (actionType === 'reschedule') {
        if (!rescheduleDate || !rescheduleTime || !rescheduleReason.trim()) {
          toast.error(
            'Please provide a valid date, time, and reason for rescheduling.'
          );
          return;
        }

        // ✅ Ensure time is formatted correctly (HH:MM)
        const convertTo24Hour = (time) => {
          if (!time) return '';
          // Handle both 24-hour and 12-hour formats
          if (time.includes('AM') || time.includes('PM')) {
            const [hour, minute] = time.split(/[: ]/);
            const isPM = time.toLowerCase().includes('pm');
            let hours24 = parseInt(hour, 10);
            if (isPM && hours24 !== 12) hours24 += 12;
            if (!isPM && hours24 === 12) hours24 = 0;
            return `${hours24.toString().padStart(2, '0')}:${minute || '00'}`;
          }
          // Already in 24-hour format
          return time;
        };

        const formattedTime = convertTo24Hour(rescheduleTime);

        const response = await dispatch(
          rescheduleAppointment({
            id: appointment.id,
            new_date: rescheduleDate,
            new_time: formattedTime,
            reason: rescheduleReason,
          })
        ).unwrap();
        toast.success(
          `Appointment rescheduled to ${format(new Date(rescheduleDate), 'MMM dd, yyyy')} at ${rescheduleTime}`
        );
      } else if (actionType === 'cancel') {
        if (!cancelReason.trim()) {
          toast.error('Please provide a reason for cancellation.');
          return;
        }

        const response = await dispatch(
          cancelAppointment({ id: appointment.id, reason: cancelReason })
        ).unwrap();
        toast.info('Appointment canceled.');
      }

      // Refresh appointments list and call onUpdate callback
      setShowConfirmDialog(false);
      setActionType('');
      setRescheduleReason('');
      setCancelReason('');
      handleClose();
      
      // Call onUpdate callback if provided to refresh parent component
      if (onUpdate) {
        await onUpdate();
      } else {
        // Fallback to dispatch if no callback provided
        dispatch(fetchAppointments({ page: 1 }));
      }
    } catch (error) {
      console.error('❌ API Call Failed:', error);
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || error?.response?.data?.error || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    }
  };

  const openConfirmDialog = (action) => {
    setActionType(action);
    setShowConfirmDialog(true);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '14px',
          padding: '24px',
          minWidth: '350px',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        sx={{ position: 'absolute', right: 10, top: 10 }}
        onClick={handleClose}
      >
        <Close />
      </IconButton>

      <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        Manage Appointment
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant='subtitle1'>
            <strong>Patient:</strong> {appointment?.patient_name || appointment?.patient?.name || appointment?.patient?.full_name || 'N/A'}
          </Typography>
          <Typography variant='subtitle1'>
            <strong>Doctor:</strong> {appointment?.doctor_name || appointment?.doctor?.name || appointment?.doctor?.full_name || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            <strong>Date:</strong> {appointment?.formatted_date || appointment?.scheduled_date ? format(new Date(appointment.scheduled_date), 'MMM dd, yyyy') : 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            <strong>Time:</strong> {appointment?.formatted_time || appointment?.scheduled_time || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            <strong>Type:</strong> {appointment?.consultation_type || appointment?.type || 'Consultation'}
          </Typography>
        </Box>

        {appointment?.status === 'Canceled' ? (
          <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: '#ffcccc' }}>
            <Typography variant='h6' sx={{ color: 'red', fontWeight: 'bold' }}>
              <Cancel sx={{ verticalAlign: 'middle', mr: 1 }} />
              Appointment Canceled
            </Typography>
            <Typography>
              <strong> {appointment.details} </strong>
            </Typography>
          </Box>
        ) : (
          <>
            {appointment?.status === 'Pending' && (
              <Button
                variant='contained'
                color='success'
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => openConfirmDialog('confirm')}
              >
                Confirm Appointment
              </Button>
            )}

            {['Pending', 'Confirmed'].includes(appointment?.status) && (
              <Button
                variant='contained'
                color='primary'
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleStartConsultation} // ✅ Navigate to Consultation Page
              >
                Start Consultation
              </Button>
            )}

            {['Pending', 'Confirmed'].includes(appointment?.status) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant='h6'>Reschedule Appointment</Typography>
                <TextField
                  label='New Date'
                  type='date'
                  fullWidth
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  sx={{ my: 2 }}
                  inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
                />
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{ display: 'block', mx: 'auto', my: 2 }}
                  />
                ) : (
                  <Select
                    fullWidth
                    value={rescheduleTime || ''}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    disabled={
                      !rescheduleDate || loading || available_slots.length === 0
                    }
                    sx={{ mb: 2 }}
                  >
                    {available_slots.length > 0 ? (
                      available_slots.map((slot, index) => (
                        <MenuItem key={index} value={slot}>
                          {slot}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No slots available</MenuItem>
                    )}
                  </Select>
                )}
                <TextField
                  label='Reason for Rescheduling'
                  fullWidth
                  multiline
                  rows={2}
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  sx={{ my: 2 }}
                  inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
                />
                <Button
                  variant='contained'
                  color='primary'
                  fullWidth
                  onClick={() => openConfirmDialog('reschedule')}
                >
                  Reschedule
                </Button>
              </Box>
            )}

            {appointment?.status === 'Pending' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant='h6'>Cancel Appointment</Typography>
                <TextField
                  label='Reason for Cancellation'
                  fullWidth
                  multiline
                  rows={2}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  sx={{ my: 2 }}
                />
                <Button
                  variant='contained'
                  color='error'
                  fullWidth
                  onClick={() => openConfirmDialog('cancel')}
                >
                  Cancel Appointment
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color='secondary'>
            No
          </Button>
          <Button onClick={handleConfirmAction} color='primary'>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AppointmentPopup;
